import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import TopNavigation from '../../components/TopNavigation';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import SearchBar from './components/SearchBar';
import BlogPostCard from './components/BlogPostCard';
import PaginationControls from './components/PaginationControls';
import EmptyState from './components/EmptyState';

import { postsService } from '../../services/postsService';

const POSTS_PER_PAGE = 9;

export default function BlogListing() {
  const [posts, setPosts] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('loading'); // loading | error | ready

  useEffect(() => {
    loadPosts();
  }, []);

  async function loadPosts() {
    try {
      setStatus('loading');
      const data = await postsService?.getPublishedPosts();
      setPosts(Array.isArray(data) ? data : []);
      setStatus('ready');
    } catch (e) {
      console.error(e);
      setStatus('error');
    }
  }

  // ---- filtering (simple, predictable) ----
  const filtered = search?.trim()
    ? posts?.filter(post => {
        const q = search?.toLowerCase();
        return (post?.title?.toLowerCase()?.includes(q) || post?.excerpt?.toLowerCase()?.includes(q));
      })
    : posts;

  // ---- pagination ----
  const totalPages = Math.max(1, Math.ceil(filtered?.length / POSTS_PER_PAGE));
  const start = (page - 1) * POSTS_PER_PAGE;
  const visiblePosts = filtered?.slice(start, start + POSTS_PER_PAGE);

  // ---- early returns ----
  if (status === 'loading') {
    return (
      <PageShell>
        <Spinner />
      </PageShell>
    );
  }

  if (status === 'error') {
    return (
      <PageShell>
        <p className="text-[#FF6B6B] text-center">Failed to load blog posts.</p>
      </PageShell>
    );
  }

  return (
    <>
      <Helmet>
        <title>Blog – Sleeve</title>
      </Helmet>
      <TopNavigation />
      <div className="content-area min-h-screen" style={{ backgroundColor: '#242423' }}>
        <div className="max-w-7xl mx-auto py-12">
          <BreadcrumbNavigation />

          <header className="mb-12 text-center">
            <h1 className="text-2xl font-semibold mb-3" style={{ color: '#E8EDDF', fontFamily: 'IBM Plex Sans, system-ui, sans-serif' }}>Technical Blog</h1>
            <p className="text-base" style={{ color: '#CFDBD5', fontFamily: 'IBM Plex Sans, system-ui, sans-serif', maxWidth: '48rem', margin: '0 auto' }}>
              Insights on systems coordination, platform engineering, and operational excellence
            </p>
          </header>

          <SearchBar searchQuery={search} onSearchChange={setSearch} />

          {visiblePosts?.length === 0 ? (
            <EmptyState searchQuery={search} />
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3" style={{ gap: '32px' }}>
                {visiblePosts?.map(post => (
                  <BlogPostCard key={post?.id} post={post} />
                ))}
              </div>

              {totalPages > 1 && (
                <PaginationControls
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ---------------- helpers ---------------- */

function PageShell({ children }) {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#242423' }}>
      <TopNavigation />
      <div className="content-area flex items-center justify-center" style={{ minHeight: '400px' }}>
        {children}
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex flex-col items-center" style={{ gap: '16px' }}>
      <div className="w-12 h-12 rounded-full animate-spin" style={{ border: '4px solid #4FB3B8', borderTopColor: 'transparent' }} />
      <p className="text-sm" style={{ color: '#9CA3AF', fontFamily: 'IBM Plex Sans, system-ui, sans-serif' }}>Loading posts…</p>
    </div>
  );
}
